import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import moment from "moment";
import { AttendanceType } from "src/redux/types/_attendance.type";
import company_logo from "../../../../images/company_logo.jpg";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 6,
  },

  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },

  companyDetails: {
    fontSize: 9,
    color: "#000",
  },

  logoWrapper: {
    position: "relative",
    width: 50,
    height: 50,
  },

  logo: {
    width: 40,
    height: 40,
  },

  trademark: {
    position: "absolute",
    right: 5,
    top: -2,
    fontSize: 12,
    fontWeight: "bold",
  },

  label: {
    fontWeight: "normal",
  },

  value: {
    fontWeight: "bold",
  },

  header: {
    marginBottom: 10,
  },

  company: {
    fontSize: 14,
    fontWeight: "bold",
  },

  address: {
    fontSize: 9,
    marginBottom: 8,
  },

  section: {
    marginBottom: 6,
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
  },

  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#ccc",
  },

  tableRow: {
    flexDirection: "row",
  },

  cellHeader: {
    flex: 1,
    padding: 6,
    backgroundColor: "#eee",
    fontWeight: "bold",
    borderRightWidth: 1,
    borderBottomWidth: 1,
  },

  cell: {
    flex: 1,
    padding: 6,
    borderRightWidth: 1,
    borderBottomWidth: 1,
  },
});

type Props = {
  data: AttendanceType[];
  fromDate?: Date;
  toDate?: Date;
};

const AttendancePDF = ({ data, fromDate, toDate }: Props) => {
  if (!data.length) return null;

  const employee = data[0];

  const total = data.length;
  const present = data.filter((d) => d.attendance_status === "FULLDAY").length;
  const absent = data.filter((d) => d.attendance_status === "ABSENT").length;
  const halfday = data.filter((d) => d.attendance_status === "HALFDAY").length;

  const logo = company_logo;

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* HEADER */}
        <View style={styles.headerRow}>
          <View>
            <Text style={styles.companyName}>
              Optim Health and Wellness (OPC) Pvt. Ltd.
            </Text>
            <Text style={styles.companyDetails}>
              H. No. 01, Karbi Namghar Path, Khagen Mahanta Path, Hengrabari,
              Barbari, Guwahati, Dispur, Assam, India.
            </Text>
            <Text style={[styles.companyDetails, { marginTop: 6 }]}></Text>
          </View>

          <View style={styles.logoWrapper}>
            {logo && <Image src={logo} style={styles.logo} />}
            <Text style={styles.trademark}>®</Text>
          </View>
        </View>

        {/* EMPLOYEE INFO */}
        <View style={styles.section}>
          <Text>
            Attendance Report for:{" "}
            <Text style={styles.value}>{employee.employee_name}</Text>
          </Text>

          <Text>
            Employee Code:{" "}
            <Text style={styles.value}>{employee.employee_code}</Text>
          </Text>

          <Text>
            Role: <Text style={styles.value}>{employee.employee_post}</Text>
          </Text>

          <Text>
            Date Range:{" "}
            <Text style={styles.value}>
              {fromDate ? moment(fromDate).format("MMM D, YYYY") : "-"} to{" "}
              {toDate ? moment(toDate).format("MMM D, YYYY") : "Latest"}
            </Text>
          </Text>
        </View>

        {/* SUMMARY */}
        <View style={{ flexDirection: "row", gap: 10 }}>
          <Text>
            Total Days: <Text style={styles.value}>{total}</Text>
          </Text>
          <Text>
            Present Days: <Text style={styles.value}>{present}</Text>
          </Text>
          <Text>
            Absent Days: <Text style={styles.value}>{absent}</Text>
          </Text>
          <Text>
            Half Days: <Text style={styles.value}>{halfday}</Text>
          </Text>
        </View>

        {/* TABLE */}
        <View style={styles.table}>
          {/* Header */}
          <View style={styles.tableRow}>
            <Text style={styles.cellHeader}>Date</Text>
            <Text style={styles.cellHeader}>Attendance Status</Text>
            <Text style={styles.cellHeader}>Check In Time</Text>
            <Text style={styles.cellHeader}>Check Out Time</Text>
          </View>

          {/* Rows */}
          {data.map((item) => (
            <View key={item.id} style={styles.tableRow}>
              <Text style={styles.cell}>
                {moment(item.date).format("MMM Do YY")}
              </Text>
              <Text style={styles.cell}>{item.attendance_status}</Text>
              <Text style={styles.cell}>
                {item.checkin_time
                  ? moment(item.checkin_time).format("LT")
                  : "-"}
              </Text>
              <Text style={styles.cell}>
                {item.checkout_time && item.attendance_status !== "HALFDAY"
                  ? moment(item.checkout_time).format("LT")
                  : "-"}
              </Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default AttendancePDF;
